/**
 * The gate, then the app. `/v1/auth/session` is asked once on boot and decides between the
 * login screen and the routes; after that any 401 from `api.ts` drops straight back here,
 * so a session that expires mid-session lands on the code field rather than on an error.
 *
 * Nothing renders while the answer is unknown: flashing the demos list at someone who
 * turns out to be signed out is worse than a blank half-second.
 */

import { useEffect, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { api, onUnauthorized } from "./api";
import { DemosPage } from "./pages/DemosPage";
import { LivePage } from "./pages/LivePage";
import { LoginPage } from "./pages/LoginPage";
import { NewDemoPage } from "./pages/NewDemoPage";
import { ReviewPage } from "./pages/ReviewPage";

export function App() {
  const [signedIn, setSignedIn] = useState<boolean | null>(null);

  useEffect(() => {
    onUnauthorized(() => setSignedIn(false));
    api<{ signed_in: boolean }>("/v1/auth/session")
      .then((session) => setSignedIn(session.signed_in))
      .catch(() => setSignedIn(false));
  }, []);

  if (signedIn === null) return null;
  if (!signedIn) return <LoginPage onSignedIn={() => setSignedIn(true)} />;

  return (
    <Routes>
      <Route path="/" element={<DemosPage />} />
      <Route path="/new" element={<NewDemoPage />} />
      <Route path="/demos/:id/review" element={<ReviewPage />} />
      <Route path="/demos/:id" element={<LivePage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
